import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["buttonType", "fluid", "size"];
import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { ButtonGroupMixin } from '../../mixins/components/ButtonGroupMixin';
import { ButtonGroupContext } from '../../js/ButtonGroupContext';
import { BUTTON_TYPES, BUTTON_SIZES } from '../../constants/BUTTON';
import CustomPropTypes from '../../util/custom-prop-types';
import { doNotForwardTransientProp } from '../../util/doNotForwardTransientProp';
export var StyledButtonGroup = /*#__PURE__*/styled('div').withConfig({
  shouldForwardProp: doNotForwardTransientProp(['conjoined', 'direction', 'fluid'])
}).withConfig({
  displayName: "StyledButtonGroup",
  componentId: "c11n-8-64-0__sc-12tlbte-0"
})(["", ""], ButtonGroupMixin);
/**
 * A collection of buttons, including [`Button`](#/Button%20components?id=button),
 * [`TextButton`](#/Button%20components?id=textbutton), and
 * [`ToggleButton`](#/Button%20components?id=togglebutton) components.
 *
 * To tell assistive technologies that buttons are grouped, you should add a
 * [group role](https://www.w3.org/TR/wai-aria-1.2/#group) and an appropriate
 * [aria-label](https://www.w3.org/TR/wai-aria-1.2/#aria-label).
 */

var ButtonGroup = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var buttonType = _ref.buttonType,
      fluid = _ref.fluid,
      size = _ref.size,
      rest = _objectWithoutProperties(_ref, _excluded);

  return /*#__PURE__*/React.createElement(ButtonGroupContext.Provider, {
    value: {
      buttonType: buttonType,
      fluid: fluid,
      size: size
    }
  }, /*#__PURE__*/React.createElement(StyledButtonGroup, _extends({
    ref: ref,
    fluid: fluid
  }, rest)));
});
ButtonGroup.displayName = 'ButtonGroup';
ButtonGroup.propTypes = {
  /**
   * An [aria-label](https://www.w3.org/TR/wai-aria-1.2/#aria-label) is required for assistive
   * technologies to announce the button group properly.
   */
  'aria-label': PropTypes.string.isRequired,

  /**
   * Sets `buttonType` for all [`Button`](#/Button%20components?id=button) children.
   */
  'buttonType': PropTypes.oneOf(Object.keys(BUTTON_TYPES)),

  /**
   * Connect the buttons by removing the space between buttons in the group. In general, you will
   * only want to do this with buttons of the same type. You can use `buttonType` to set the type
   * to be the same for all buttons.
   */
  'conjoined': PropTypes.bool,

  /**
   * The direction in which the buttons should be aligned. Buttons will be fluid when the
   * direction is set to `column`.
   *
   * Supports [inline media query objects](#/Media%20queries)
   */
  'direction': CustomPropTypes.inlineMediaQuery(PropTypes.oneOf(['row', 'column'])),

  /**
   * The button group will expand to fill its parent container.
   *
   * Supports [inline media query objects](#/Media%20queries)
   */
  'fluid': CustomPropTypes.inlineMediaQuery(PropTypes.bool),

  /**
   * Sets the `role` of the button group. By default, this is set to
   * ["group"](https://www.w3.org/TR/wai-aria-1.2/#group), but "toolbar" might be more
   * appropriate if the button group is used in a toolbar.
   */
  'role': PropTypes.string,

  /**
   * Sets `size` for all [`Button`](#/Button%20components?id=button) children.
   */
  'size': PropTypes.oneOf(Object.keys(BUTTON_SIZES))
};
ButtonGroup.defaultProps = {
  direction: 'row',
  role: 'group'
};
/** @component */

export default ButtonGroup;